import { useState } from "react";
import { Info } from "lucide-react";
import ChanceBadge from "@/components/app/ChanceBadge";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BAND_DEFS, BAND_FACTORS, BAND_LIMITS, NOT_A_CHANCE, bandDef } from "./bandDefinitions";

const FOCUS = "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95] focus-visible:ring-offset-1";

// Grade-match pill + (i): both open the same click/tap/keyboard popover with every band's definition.
export function BandPill({ bucket, label, rowId }) {
  const [open, setOpen] = useState(false);
  const cur = bandDef(bucket);
  const name = cur?.pill || label;
  const aria = `What does ${name} mean?`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <span className="inline-flex items-center">
          <button type="button" onClick={() => setOpen(true)} aria-label={aria} aria-haspopup="dialog" aria-expanded={open}
            data-testid={`band-pill-${rowId}`} className={`rounded-full ${FOCUS}`}>
            <ChanceBadge bucket={bucket} label={label} />
          </button>
          <PopoverTrigger asChild>
            <button type="button" aria-label={aria} data-testid={`band-info-${rowId}`}
              className={`-my-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-[#4C1D95] hover:bg-[#4C1D95]/10 ${FOCUS}`}>
              <Info className="h-[18px] w-[18px]" aria-hidden="true" />
            </button>
          </PopoverTrigger>
        </span>
      </PopoverAnchor>
      <PopoverContent align="end" collisionPadding={{ top: 136, bottom: 12, left: 12, right: 12 }} role="dialog" aria-label={aria}
        data-testid={`band-popover-${rowId}`}
        className="w-[min(22rem,calc(100vw-24px))] max-h-[min(70vh,30rem)] overflow-y-auto border-stone-200 bg-white p-4 text-[12.5px] leading-snug text-stone-700 shadow-lg">
        <BandDefinitions current={cur} />
      </PopoverContent>
    </Popover>
  );
}

function BandDefinitions({ current }) {
  return (
    <div className="space-y-3">
      {current && (
        <p data-testid="band-popover-current">
          <span className="font-semibold text-stone-900">This course: {current.pill}</span>{" "}
          <span className="text-stone-500">({current.band} band)</span>. {current.meaning} {current.caveat || ""}
        </p>
      )}
      <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-900" data-testid="band-popover-not-chance">{NOT_A_CHANCE}</p>
      <ul className="space-y-1.5" data-testid="band-popover-list">
        {BAND_DEFS.map((b) => (
          <li key={b.key} data-testid={`band-popover-def-${b.key}`} className={b.key === current?.key ? "font-medium text-stone-900" : ""}>
            <span className="font-semibold">{b.pill}</span> <span className="text-stone-500">({b.band})</span>: {b.meaning}
          </li>
        ))}
      </ul>
      <p>It compares your profile with the course's entry requirements using {BAND_FACTORS}.</p>
      <ul className="list-disc pl-4 space-y-1 text-stone-600">{BAND_LIMITS.map((l) => <li key={l}>{l}</li>)}</ul>
      <p className="text-stone-600">The ~% beside it is a separate, rough estimate based on the university-wide acceptance rate. Tap it to see how it's worked out.</p>
    </div>
  );
}
